/**
 * Actionable guidance for the Embedded Signup step where a customer abandoned
 * the Meta popup (INT-1217).
 *
 * INT-1216 captures Meta's `current_step` on every `CANCEL`; this module turns
 * it into something the customer can act on, replacing the generic
 * "agenda una sesión" as the first thing they read.
 *
 * ## Why this is a hand-authored table and not a derivation
 *
 * INT-1217's description asserts that Meta's error-catalog categories are named
 * after the flow steps, and proposes joining the two mechanically. They are not.
 * Meta documents six `current_step` values in SCREAMING_SNAKE_CASE, while the
 * catalog's ~20 headings are prose ("Phone setup errors", "WhatsApp Business app
 * user onboarding", "Shared accounts"). There is no join key, and several steps
 * span multiple categories — `BUSINESS_ACCOUNT_SELECTION` alone touches Meta
 * Business Suite account creation, business account limits, business account
 * names and shared accounts.
 *
 * The premise does hold for the one step we have actually observed in
 * production, `WHATSAPP_BUSINESS_APP_ONBOARDING_PHONE_CREATION` ↔ "WhatsApp
 * Business app user onboarding" (home of error 2655095), which is presumably
 * where the generalization came from.
 *
 * So the mapping below is editorial: each entry was written against the error
 * family a step plausibly fails in. Correct the copy freely — it is the point of
 * the file — but keep the shape:
 *
 * 1. Keys are RAW step strings, matched exactly. Meta ships steps it has not
 *    documented (that is how we got the one above), so there is deliberately no
 *    union type and no exhaustive switch anywhere in the consumer chain.
 * 2. `DEFAULT_EMBEDDED_SIGNUP_STEP_GUIDANCE` is mandatory. An unrecognized step
 *    must degrade to it, never to a blank slot.
 * 3. Entries carry an i18n key AND an inline Spanish default, mirroring
 *    `ConnectionFailedError`'s `t(key, default)` usage, so a missing translation
 *    degrades to readable copy instead of a raw key.
 *
 * Sources:
 * - Steps + error families: https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/errors/
 * - Envelope: https://developers.facebook.com/docs/whatsapp/embedded-signup/implementation
 *
 * NOTE: These are Meta's identifiers. Our own backend's stable codes (`wba-*`,
 * sourced from whatsapp-business-service's InternalErrorMapper.go) live in
 * `whatsappBusinessErrorCodes.constants.ts` and are a separate namespace — do
 * not mix them.
 */

export type tEmbeddedSignupStepGuidance = {
  /** i18n key under the whatsappConnect namespace. */
  titleKey: string;
  /** Inline Spanish fallback used when the key is missing. */
  titleDefault: string;
  subtitleKey: string;
  subtitleDefault: string;
};

export const EMBEDDED_SIGNUP_STEPS = {
  BUSINESS_ACCOUNT_SELECTION: 'BUSINESS_ACCOUNT_SELECTION',
  WABA_PHONE_PROFILE_PICKER: 'WABA_PHONE_PROFILE_PICKER',
  WHATSAPP_BUSINESS_PROFILE_SETUP: 'WHATSAPP_BUSINESS_PROFILE_SETUP',
  PHONE_NUMBER_SETUP: 'PHONE_NUMBER_SETUP',
  PHONE_NUMBER_VERIFICATION: 'PHONE_NUMBER_VERIFICATION',
  PERMISSIONS: 'PERMISSIONS',
  SAVE: 'SAVE',
  /**
   * Observed in production but absent from Meta's documented step list — the
   * reason INT-1216 stores the step as an unconstrained string and this table is
   * keyed by raw string. Corresponds to the "WhatsApp Business app user
   * onboarding" family, where error 2655095 ("your number isn't registered with
   * WhatsApp Business") lives.
   */
  WHATSAPP_BUSINESS_APP_ONBOARDING_PHONE_CREATION:
    'WHATSAPP_BUSINESS_APP_ONBOARDING_PHONE_CREATION',
} as const;

export const DEFAULT_EMBEDDED_SIGNUP_STEP_GUIDANCE: tEmbeddedSignupStepGuidance = {
  titleKey: 'failedClosed.steps.default.title',
  titleDefault: 'Te detuviste antes de terminar la conexión',
  subtitleKey: 'failedClosed.steps.default.subtitle',
  subtitleDefault:
    'Vuelve a intentarlo y completa todos los pasos que Meta te muestre sin cerrar la ventana. Si se detiene otra vez en el mismo punto, un asesor puede acompañarte.',
};

/**
 * A Map rather than an object literal on purpose: `record[step]` on a plain
 * object would resolve inherited keys, so a step literally named `constructor`
 * or `toString` would return a prototype member instead of missing cleanly.
 */
const STEP_GUIDANCE = new Map<string, tEmbeddedSignupStepGuidance>([
  [
    EMBEDDED_SIGNUP_STEPS.SAVE,
    {
      titleKey: 'failedClosed.steps.save.title',
      titleDefault: 'Te detuviste en el último paso, al guardar la conexión',
      subtitleKey: 'failedClosed.steps.save.subtitle',
      subtitleDefault:
        'Vuelve a intentarlo y mantén la ventana de Meta abierta hasta que termine de guardar. Si la cierras antes, la conexión no se completa.',
    },
  ],
  [
    EMBEDDED_SIGNUP_STEPS.BUSINESS_ACCOUNT_SELECTION,
    {
      titleKey: 'failedClosed.steps.businessAccountSelection.title',
      titleDefault: 'Te detuviste al elegir tu cuenta de negocio',
      subtitleKey: 'failedClosed.steps.businessAccountSelection.subtitle',
      subtitleDefault:
        'Vuelve a intentarlo y selecciona la cuenta de Meta Business que ya usas para tu negocio. Si no aparece en la lista, revisa en business.facebook.com que tu usuario tenga acceso de administrador.',
    },
  ],
  [
    EMBEDDED_SIGNUP_STEPS.WABA_PHONE_PROFILE_PICKER,
    {
      titleKey: 'failedClosed.steps.wabaPhoneProfilePicker.title',
      titleDefault: 'Te detuviste al elegir tu cuenta de WhatsApp Business',
      subtitleKey: 'failedClosed.steps.wabaPhoneProfilePicker.subtitle',
      subtitleDefault:
        'Vuelve a intentarlo y elige la cuenta de WhatsApp Business y el número que quieres conectar. Si todavía no tienes una, Meta te deja crearla en ese mismo paso.',
    },
  ],
  [
    EMBEDDED_SIGNUP_STEPS.WHATSAPP_BUSINESS_PROFILE_SETUP,
    {
      titleKey: 'failedClosed.steps.whatsappBusinessProfileSetup.title',
      titleDefault: 'Te detuviste al configurar el perfil de tu negocio',
      subtitleKey: 'failedClosed.steps.whatsappBusinessProfileSetup.subtitle',
      subtitleDefault:
        'Vuelve a intentarlo y completa el nombre visible y la categoría de tu negocio. Meta rechaza nombres genéricos o que incluyan teléfonos, correos o direcciones web.',
    },
  ],
  [
    EMBEDDED_SIGNUP_STEPS.PHONE_NUMBER_SETUP,
    {
      titleKey: 'failedClosed.steps.phoneNumberSetup.title',
      titleDefault: 'Te detuviste al registrar tu número',
      subtitleKey: 'failedClosed.steps.phoneNumberSetup.subtitle',
      subtitleDefault:
        'Vuelve a intentarlo con un número que no esté en uso por otra cuenta de WhatsApp. Si ese número ya tiene WhatsApp o WhatsApp Business instalado, elimina primero la cuenta desde la app o usa un número distinto.',
    },
  ],
  [
    EMBEDDED_SIGNUP_STEPS.PHONE_NUMBER_VERIFICATION,
    {
      titleKey: 'failedClosed.steps.phoneNumberVerification.title',
      titleDefault: 'Te detuviste al verificar tu número',
      subtitleKey: 'failedClosed.steps.phoneNumberVerification.subtitle',
      subtitleDefault:
        'Vuelve a intentarlo con el teléfono a la mano: Meta envía un código por SMS o llamada y caduca en pocos minutos. Si no llega, espera un momento antes de pedir otro — Meta limita los intentos seguidos.',
    },
  ],
  [
    EMBEDDED_SIGNUP_STEPS.PERMISSIONS,
    {
      titleKey: 'failedClosed.steps.permissions.title',
      titleDefault: 'Te detuviste al conceder los permisos',
      subtitleKey: 'failedClosed.steps.permissions.subtitle',
      subtitleDefault:
        'Vuelve a intentarlo y acepta todos los permisos que Meta te pida, incluido el acceso a tu cuenta de WhatsApp Business. Si dejas alguno fuera, no podremos enviar ni recibir mensajes por ti.',
    },
  ],
  [
    EMBEDDED_SIGNUP_STEPS.WHATSAPP_BUSINESS_APP_ONBOARDING_PHONE_CREATION,
    {
      titleKey: 'failedClosed.steps.whatsappBusinessAppOnboarding.title',
      titleDefault: 'Tu número todavía no está listo en WhatsApp Business',
      subtitleKey: 'failedClosed.steps.whatsappBusinessAppOnboarding.subtitle',
      subtitleDefault:
        'Meta se detuvo al dar de alta tu número en la app de WhatsApp Business. Si ese número aún no está registrado ahí, regístralo primero desde la app de WhatsApp Business y vuelve a intentarlo, o conecta un número distinto.',
    },
  ],
]);

/**
 * Guidance for a reported step. Unknown, empty and non-string inputs all resolve
 * to the default entry, so every caller always has copy to render.
 *
 * Matching is exact: no case folding and no prefix matching. A step that differs
 * only in case is a step we have not seen, and guessing at it would be worse
 * than the honest default.
 */
export function resolveEmbeddedSignupStepGuidance(
  step: string | null | undefined,
): tEmbeddedSignupStepGuidance {
  if (typeof step !== 'string') return DEFAULT_EMBEDDED_SIGNUP_STEP_GUIDANCE;
  const trimmed = step.trim();
  if (!trimmed) return DEFAULT_EMBEDDED_SIGNUP_STEP_GUIDANCE;
  return STEP_GUIDANCE.get(trimmed) ?? DEFAULT_EMBEDDED_SIGNUP_STEP_GUIDANCE;
}

/** Every entry, for tests that assert locale coverage. */
export function listEmbeddedSignupStepGuidance(): tEmbeddedSignupStepGuidance[] {
  return [...STEP_GUIDANCE.values(), DEFAULT_EMBEDDED_SIGNUP_STEP_GUIDANCE];
}
